import React, { Suspense, lazy, useEffect } from "react";
import {
  Route,
  RouterProvider,
  Outlet,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation,
} from "react-router-dom";
import ConnectionStatus from "./components/ui/ConnectionStatus";
import { LoadingPage } from "./components/ui/LoadingSpinner";
import { useConnection } from "./hooks/useConnection";
import { PUBLIC_THEME_ROUTES, THEME_STORAGE_KEYS } from "./constants/theme";
import { initializeAnalytics } from "./lib/firebase";

const Navigation = lazy(() => import("./components/Navigation"));
const PrivateRoute = lazy(() => import("./components/auth/PrivateRoute"));
const AdminRoute = lazy(() => import("./components/auth/AdminRoute"));
const Landing = lazy(() => import("./pages/Landing"));
const Login = lazy(() => import("./pages/Login"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const SmartTagManager = lazy(() => import("./pages/SmartTagManager"));
const SnowGearDashboard = lazy(
  () => import("./pages/snow-gear/SnowGearDashboard"),
);
const SnowGearSubcategoryPage = lazy(
  () => import("./pages/snow-gear/SnowGearSubcategoryPage"),
);
const Analytics = lazy(() => import("./pages/Analytics"));
const Profile = lazy(() => import("./pages/Profile"));
const Membership = lazy(() => import("./pages/Membership"));
const Admin = lazy(() => import("./pages/Admin"));
const PublicVCard = lazy(() => import("./pages/PublicVCard"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ValidateEmail = lazy(() => import("./pages/ValidateEmail"));

const ThemeController: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const isPublicRoute = PUBLIC_THEME_ROUTES.has(location.pathname);
    const primaryStorageKey = isPublicRoute
      ? THEME_STORAGE_KEYS.public
      : THEME_STORAGE_KEYS.app;
    const storage = window.localStorage;
    const keysToCheck = [
      primaryStorageKey,
      THEME_STORAGE_KEYS.app,
      THEME_STORAGE_KEYS.public,
      "darkMode",
    ];
    const storedValue =
      keysToCheck
        .map((key) => storage.getItem(key))
        .find((value) => value === "true" || value === "false") ?? null;

    const shouldBeDark =
      storedValue === null ? isPublicRoute : storedValue === "true";

    document.documentElement.classList.toggle("dark", shouldBeDark);
    const serializedValue = shouldBeDark ? "true" : "false";
    const keysToPersist = new Set(keysToCheck);
    keysToPersist.forEach((key) => storage.setItem(key, serializedValue));
  }, [location.pathname]);

  return null;
};

const ProtectedPageLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <PrivateRoute>
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <Navigation />
      <main className="container mx-auto px-4 py-8">{children}</main>
    </div>
  </PrivateRoute>
);

const AppLayout: React.FC = () => {
  const isOnline = useConnection();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void initializeAnalytics();
    }, 1500);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  return (
    <div className="min-h-screen">
      <ThemeController />
      <Suspense fallback={<LoadingPage message="Loading page..." />}>
        <Outlet />
      </Suspense>
      {!isOnline && <ConnectionStatus isOnline={isOnline} />}
    </div>
  );
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AppLayout />}>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route
        path="/qr/snowgear/:subCategory/:userId"
        element={<PublicVCard />}
      />
      <Route
        path="/qr/:category/:userId/:subCategory"
        element={<PublicVCard />}
      />
      <Route path="/qr/:category/:userId" element={<PublicVCard />} />
      <Route path="/qr/:id" element={<PublicVCard />} />
      <Route path="/users/:userId/:category" element={<PublicVCard />} />
      <Route path="/users/:userId" element={<PublicVCard />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedPageLayout>
            <Dashboard />
          </ProtectedPageLayout>
        }
      />
      <Route
        path="/dashboard/main"
        element={
          <ProtectedPageLayout>
            <Dashboard />
          </ProtectedPageLayout>
        }
      />
      <Route
        path="/smart-tags"
        element={
          <ProtectedPageLayout>
            <SmartTagManager />
          </ProtectedPageLayout>
        }
      />
      <Route
        path="/smart-tags/:category"
        element={
          <ProtectedPageLayout>
            <SmartTagManager />
          </ProtectedPageLayout>
        }
      />
      <Route
        path="/smart-tags/snow-gear"
        element={
          <ProtectedPageLayout>
            <SnowGearDashboard />
          </ProtectedPageLayout>
        }
      />
      <Route
        path="/smart-tags/snow-gear/:subcategory"
        element={
          <ProtectedPageLayout>
            <SnowGearSubcategoryPage />
          </ProtectedPageLayout>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedPageLayout>
            <Analytics />
          </ProtectedPageLayout>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedPageLayout>
            <Profile />
          </ProtectedPageLayout>
        }
      />
      <Route
        path="/membership"
        element={
          <ProtectedPageLayout>
            <Membership />
          </ProtectedPageLayout>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedPageLayout>
            <AdminRoute>
              <Admin />
            </AdminRoute>
          </ProtectedPageLayout>
        }
      />
      <Route path="/validate-email" element={<ValidateEmail />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/about" element={<About />} />
      <Route path="*" element={<NotFound />} />
    </Route>,
  ),
);

const App: React.FC = () => <RouterProvider router={router} />;

export default App;
